<template>
  <v-card
    class="idea-card"
    dark
    flat
    role="button"
    tabindex="0"
    @click="$emit('click')"
    @keydown.enter="$emit('click')"
    @keydown.space.prevent="$emit('click')">
    <div v-if="firstImage" class="idea-image-wrap">
      <SecuredMedia :src="attachmentPath(firstImage)" media-type="image" />
    </div>
    <div v-else-if="firstAttachment" class="idea-image-wrap idea-image-placeholder">
      <span :style="{ color: categoryColor(firstAttachment) }"><v-icon>{{ attachmentIcon(firstAttachment) }}</v-icon></span>
    </div>

    <v-card-text class="idea-body">
      <div class="idea-meta">
        <div class="idea-author">
          <v-avatar size="32" color="#252826">
            <span>{{ initials }}</span>
          </v-avatar>
          <span>{{ idea.authorName || "Unbekannt" }}</span>
        </div>
        <time>{{ formatDate(idea.createdAt) }}</time>
      </div>

      <h3>{{ idea.title }}</h3>
      <p class="line-clamp-3">{{ idea.description }}</p>

      <div class="idea-actions">
        <v-btn text class="idea-action" :color="idea.likedByMe ? '#ff6b78' : '#b8b8bc'" @click.stop="$emit('like', idea._id)">
          <v-icon left>{{ idea.likedByMe ? "mdi-heart" : "mdi-heart-outline" }}</v-icon>
          {{ idea.likeCount != null ? idea.likeCount : (idea.likes ? idea.likes.length : 0) }}
        </v-btn>
        <v-btn text class="idea-action" color="#b8b8bc" @click.stop="$emit('click')">
          <v-icon left>mdi-comment-outline</v-icon>
          {{ idea.commentCount != null ? idea.commentCount : (idea.comments ? idea.comments.length : 0) }}
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import api from "@/api"
import SecuredMedia from "./SecuredMedia.vue"

export default {
  name: "IdeaCard",
  components: { SecuredMedia },
  props: {
    idea: { type: Object, required: true },
  },
  computed: {
    firstAttachment() {
      return this.idea.attachments && this.idea.attachments[0]
    },
    firstImage() {
      const a = this.firstAttachment
      return a && a.mimeType && a.mimeType.startsWith("image/") ? a : null
    },
    initials() {
      const n = this.idea.authorName || ""
      const parts = n.trim().split(/\s+/)
      return ((parts[0] || "?")[0] + (parts[1] ? parts[1][0] : "")).toUpperCase()
    },
  },
  methods: {
    attachmentPath(att) {
      return api.akademie.getIdeaAttachmentPath(this.idea._id, att.fileId)
    },
    attachmentIcon(att) {
      if (!att || !att.mimeType) return "mdi-file-outline"
      if (att.mimeType.startsWith("video/")) return "mdi-video-outline"
      if (att.mimeType.startsWith("audio/")) return "mdi-music-note-outline"
      if (att.mimeType === "application/pdf") return "mdi-file-pdf-box"
      return "mdi-file-outline"
    },
    categoryColor(att) {
      if (!att || !att.mimeType) return "#bdbdbd"
      if (att.mimeType.startsWith("video/")) return "#ef5350"
      if (att.mimeType.startsWith("audio/")) return "#ab47bc"
      if (att.mimeType === "application/pdf") return "#e53935"
      return "#bdbdbd"
    },
    formatDate(date) {
      if (!date) return ""
      return new Date(date).toLocaleDateString("de-CH")
    },
  },
}
</script>

<style scoped>
.idea-card {
  break-inside: avoid;
  margin-bottom: 1rem;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1.125rem !important;
  outline: none;
  background: rgba(255, 255, 255, 0.035) !important;
  cursor: pointer;
  transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.idea-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.idea-image-wrap {
  max-height: 17.5rem;
  overflow: hidden;
  border-bottom: 1px solid rgba(255, 255, 255, 0.075);
}

.idea-image-wrap :deep(.secured-media-content) {
  display: block;
  width: 100%;
  border-radius: 0;
}

.idea-image-placeholder {
  display: grid;
  height: 8.75rem;
  place-items: center;
  background: rgba(0, 0, 0, 0.18);
}

.idea-image-placeholder > span {
  display: grid;
  width: 3.5rem;
  height: 3.5rem;
  place-items: center;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.055);
}

.idea-image-placeholder .v-icon {
  color: currentColor;
  font-size: 1.75rem;
}

.idea-body {
  padding: 1rem !important;
}

.idea-meta,
.idea-author,
.idea-actions {
  display: flex;
  align-items: center;
}

.idea-meta,
.idea-actions {
  justify-content: space-between;
  gap: 0.75rem;
}

.idea-author {
  min-width: 0;
  gap: 0.5rem;
}

.idea-author .v-avatar span {
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 750;
}

.idea-author > span,
.idea-meta time {
  overflow: hidden;
  color: #a3a3a8;
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.idea-body h3 {
  margin: 0.875rem 0 0;
  color: #f4f4f5;
  font-size: 1rem;
  line-height: 1.4;
}

.idea-body > p {
  margin: 0.5rem 0 0;
  color: #b8b8bc;
  font-size: 0.875rem;
  line-height: 1.55;
  white-space: break-spaces;
  overflow-wrap: anywhere;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.idea-actions {
  margin: 0.875rem -0.5rem -0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
}

.idea-action {
  min-width: 2.75rem !important;
  min-height: 2.75rem !important;
  border-radius: 0.75rem !important;
  font-size: 0.875rem !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.idea-action .v-icon {
  font-size: 1.125rem;
}

@media (hover: hover) and (pointer: fine) {
  .idea-card:hover {
    transform: translateY(-2px);
    border-color: rgba(255, 255, 255, 0.16);
    background: rgba(255, 255, 255, 0.055) !important;
  }
}
</style>
